import { useCallback, useRef } from "react";
import { usePttCapture } from "../../usePttCapture";
import type { PttSidecarConfig } from "../../pttSidecarClient";
import { primeBrowserAudio } from "../browserAudio";
import type { PttTransport } from "./types";
import type { PttTurnSession } from "./usePttTurns";

const IDLE: PttTransport = {
  startRecording: () => undefined,
  stopRecording: () => undefined,
  isRecording: () => false,
  keyMode: "hold",
};

/** MediaRecorder blob -> Voicebox / speaches transcribe. Hold-to-talk UX. */
export function useBlobSidecarTransport(
  config: PttSidecarConfig | null,
  session: PttTurnSession,
  transcribe: (blob: Blob) => Promise<string>,
): PttTransport {
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const transcribeRef = useRef(transcribe);
  transcribeRef.current = transcribe;

  const enabled = config !== null && config.provider !== "aws";

  const processBlob = useCallback(async (blob: Blob) => {
    const s = sessionRef.current;
    if (s.busyRef.current) return;
    s.busyRef.current = true;
    s.setProcessing();
    s.setError(null);
    try {
      const query = await transcribeRef.current(blob);
      await s.handleResult(query);
    } catch (err) {
      s.setError(err instanceof Error ? err.message : "PTT error");
    } finally {
      s.busyRef.current = false;
      s.setIdle();
    }
  }, []);

  const capture = usePttCapture({
    onRecordingStart: () => {
      const s = sessionRef.current;
      if (s.busyRef.current) return;
      primeBrowserAudio();
      s.setRecording();
      s.setError(null);
    },
    onRecordingStop: (blob) => {
      void processBlob(blob);
    },
    onError: (msg) => {
      sessionRef.current.setError(msg);
      sessionRef.current.setIdle();
    },
  });

  if (!enabled) return IDLE;

  return {
    ...capture,
    keyMode: "hold",
  };
}
